import PropTypes from 'prop-types';
import { useEffect, Fragment, useState, useContext } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Typography,
    IconButton,
    Divider,
    OutlinedInput,
    InputAdornment
} from '@mui/material';
import { Close } from '@mui/icons-material';
import useAuth from 'hooks/useAuth';
import { DEFAULT_LOGO_URL } from 'constants';
import { ModalContext } from 'contexts/ModalContext';
import { useDispatch } from 'store';
import { setBackDropOpen } from 'store/slices/backdrop';
import { useNavigate } from 'react-router-dom';
import { IconCheck, IconSearch } from '@tabler/icons-react';
import { useTheme } from '@mui/material/styles';
import DeploymentLogoImage from '../components/DeploymentLogoImage';

export default function ChooseActiveDeployment({ toggleDialog = false, onClose = () => {} }) {
    const theme = useTheme();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const { deployments = [], processReInitDeployment } = useAuth();
    const [deploymentList, setDeploymentList] = useState(deployments);
    const activeDeployment = localStorage.getItem('active-deployment') || '';
    const { setOpenInfoModal } = useContext(ModalContext);

    useEffect(() => {
        if (toggleDialog) {
            setOpen(true);
            setDeploymentList(deployments);
        }
    }, [toggleDialog, deployments]);

    const handleClose = () => {
        onClose();
    };

    const handleChooseDeployment = async (item) => {
        const { deployment } = item;
        if (deployment === activeDeployment) {
            setOpenInfoModal({
                isOpen: true,
                type: 'error',
                title: 'Cannot select current active deployment.'
            });
            return;
        }

        onClose();

        // show backdrop
        await dispatch(
            setBackDropOpen({
                open: true,
                message: 'Loading ...'
            })
        );

        // add delay 1 seconds for animation
        setTimeout(async () => {
            // create an API to create another session
            await processReInitDeployment(deployment);

            // reload the route page
            navigate(0);
        }, 1000);
    };

    const handleSearchDeployment = (search) => {
        search = search.toLowerCase();
        const list = [...deployments].filter(
            (item) => item.deployment.toLowerCase().includes(search) || item.displayName.toLowerCase().includes(search)
        );
        setDeploymentList(list);
    };

    return (
        <Fragment>
            <Dialog
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        minWidth: {
                            xs: '90%',
                            sm: '60%',
                            md: '45%',
                            lg: '35%',
                            xl: '20%'
                        }
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
                        <Box>
                            <Typography variant="h2" component="span" sx={{ fontWeight: 500 }}>
                                Switch Deployment
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
                                <Close />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <Divider />
                <DialogContent component="div" className="KWA" sx={{ p: 0 }}>
                    <DialogContentText component="div" sx={{ m: 0 }}>
                        <Grid container justifyContent="flex-end" sx={{ py: 2, pr: 4, pl: 3 }}>
                            <Grid item xs={12}>
                                <OutlinedInput
                                    placeholder="Search Deployment"
                                    inputProps={{
                                        'aria-label': 'weight'
                                    }}
                                    onKeyUp={(e) => handleSearchDeployment(e.target.value)}
                                    fullWidth
                                    endAdornment={
                                        <>
                                            <InputAdornment position="end">
                                                <IconSearch stroke={1.5} size="16px" color={theme.palette.grey[500]} />
                                            </InputAdornment>
                                        </>
                                    }
                                />
                            </Grid>
                        </Grid>
                        {deploymentList.length === 0 && (
                            <Box display="flex" justifyContent="center" sx={{ py: 2 }}>
                                <Typography>No Results Found</Typography>
                            </Box>
                        )}
                        {deploymentList.map((item, i) => (
                            <Grid
                                key={i}
                                container
                                justifyContent={'center'}
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5'
                                    },

                                    transition: 'background-color 0.2s ease'
                                }}
                                onClick={() => handleChooseDeployment(item)}
                            >
                                <Grid item xs={12} sx={{ py: 1.2, pl: 3, pr: 4 }}>
                                    <Grid container>
                                        <Grid item xs={2}>
                                            <DeploymentLogoImage src={item?.logo || DEFAULT_LOGO_URL} alt={item?.deployment || ''} />
                                        </Grid>
                                        <Grid item xs={item.deployment === activeDeployment ? 9 : 10} display="grid" alignItems="center">
                                            <Typography variant="h4" component="span" sx={{ fontWeight: 500 }}>
                                                {item?.displayName || ''}
                                            </Typography>
                                            <Typography variant="subtitle2">{item?.deployment || ''}</Typography>
                                        </Grid>
                                        {item.deployment === activeDeployment && (
                                            <Grid item xs={1} display="flex" justifyContent="center" alignItems="center">
                                                <IconCheck size={30} color={theme.palette.secondary.main} />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                        ))}
                    </DialogContentText>
                </DialogContent>
            </Dialog>
        </Fragment>
    );
}

ChooseActiveDeployment.propTypes = {
    toggleDialog: PropTypes.bool,
    onClose: PropTypes.func
};
